/**
 * A vendor's own assigned work orders — `work_orders_vendor_select` scopes
 * this to jobs assigned to them, any status (not just completed — job
 * tracking is the point, not just payment status; see that policy's own
 * comment in rls-policies.sql). Same unfiltered-`useList` shape every other
 * portal table in this app uses: RLS decided the answer, this just renders
 * it. Read-only — no status/note editing here, same as every portal actor
 * except the two named exceptions elsewhere in this app.
 */

import { useList } from '@refinedev/core';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { humanizeEnum } from '../../../components/Form.js';

interface VendorWorkOrderRow {
  id: string;
  title: string;
  status: string;
  priority: string;
  propertyName: string;
  unitLabel: string;
  cost: string | null;
  scheduledOn: string | null;
  completedAt: string | null;
}

export function VendorWorkOrderList() {
  const { data, isLoading, error } = useList<VendorWorkOrderRow>({
    resource: 'work-orders',
    pagination: { mode: 'off' },
  });

  const columns: Column<VendorWorkOrderRow>[] = [
    {
      key: 'title',
      header: 'Job',
      render: (row) => (
        <>
          <strong>{row.title}</strong>
          <div className="muted">
            {row.unitLabel}, {row.propertyName}
          </div>
        </>
      ),
    },
    {
      key: 'priority',
      header: 'Priority',
      render: (row) => <span className="pill">{humanizeEnum(row.priority)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <span className={`pill${row.status === 'COMPLETED' ? ' pill--positive' : ''}`}>
          {humanizeEnum(row.status)}
        </span>
      ),
    },
    {
      key: 'scheduled',
      header: 'Scheduled',
      render: (row) => row.scheduledOn ?? '—',
    },
    {
      key: 'cost',
      header: 'Cost',
      numeric: true,
      render: (row) => (row.cost ? `$${row.cost}` : '—'),
    },
  ];

  return (
    <section className="stack">
      <h1>Your work orders</h1>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        emptyMessage="No work orders assigned to you yet."
      />
    </section>
  );
}
